export interface FavouriteRuntimeConfig {
    ajax_url?: string;
    nonce?: string;
    can_toggle?: boolean;
    enabled?: boolean;
    groups_enabled?: boolean;
    default_group_name?: string;
    groups?: string[];
}

export interface FavouriteGroupSummary {
    name: string;
    slug: string;
    count: number;
    is_default: boolean;
}

export interface FavouriteChangeDetail {
    productId?: number;
    product_id?: number;
    isFavourite?: boolean | number | string;
    is_favourite?: boolean | number | string;
    buttonText?: string;
    button_text?: string;
    iconClass?: string;
    icon_class?: string;
    message?: string;
    groupName?: string;
    group_name?: string;
    groupSlug?: string;
    group_slug?: string;
}

interface ApiSettingsConfig {
    ajax_url?: string;
    favourites?: FavouriteRuntimeConfig;
    features?: {
        favourites?: FavouriteRuntimeConfig;
    };
}

interface RuntimeWindow extends Window {
    wekaFavourites?: FavouriteRuntimeConfig;
    wekaApiSettings?: ApiSettingsConfig;
}

export const FAVOURITE_EVENT_NAME = 'weka:favourite-changed';

export const toBool = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    const normalized = String(value || '').trim().toLowerCase();
    return ['1', 'true', 'yes', 'on'].includes(normalized);
};

export const getFavouritesConfig = (): FavouriteRuntimeConfig => {
    if (typeof window === 'undefined') {
        return {};
    }

    const runtimeWindow = window as RuntimeWindow;
    const apiConfig = runtimeWindow.wekaApiSettings?.favourites
        || runtimeWindow.wekaApiSettings?.features?.favourites
        || {};
    const favouritesConfig = runtimeWindow.wekaFavourites || {};

    return {
        ajax_url: runtimeWindow.wekaApiSettings?.ajax_url || apiConfig.ajax_url || favouritesConfig.ajax_url,
        ...apiConfig,
        ...favouritesConfig,
    };
};

export const dispatchFavouriteChange = (detail: FavouriteChangeDetail): void => {
    document.dispatchEvent(new CustomEvent<FavouriteChangeDetail>(FAVOURITE_EVENT_NAME, {detail}));
};

const getAjaxUrl = (): string => {
    const configuredUrl = String(getFavouritesConfig().ajax_url || '').trim();
    return configuredUrl !== '' ? configuredUrl : '/wp-admin/admin-ajax.php';
};

const normalizeResponseMessage = (payload: unknown, fallback: string): string => {
    if (!payload || typeof payload !== 'object') {
        return fallback;
    }

    const record = payload as Record<string, unknown>;
    const directMessage = typeof record.message === 'string' ? record.message.trim() : '';
    if (directMessage !== '') {
        return directMessage;
    }

    const nested = record.data;
    if (nested && typeof nested === 'object') {
        const nestedMessage = typeof (nested as Record<string, unknown>).message === 'string'
            ? String((nested as Record<string, unknown>).message).trim()
            : '';
        if (nestedMessage !== '') {
            return nestedMessage;
        }
    }

    return fallback;
};

export async function fetchFavouritesData<T>(params: Record<string, string | number | undefined> = {}): Promise<T> {
    const query = new URLSearchParams({action: 'get_favourites_data'});

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || String(value).trim() === '') {
            return;
        }
        query.set(key, String(value));
    });

    const response = await fetch(`${getAjaxUrl()}?${query.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
        },
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success !== true) {
        throw new Error(normalizeResponseMessage(payload, 'Failed to load favourites.'));
    }

    return payload.data as T;
}

export async function postFavouriteAction<T>(
    action: string,
    fields: Record<string, string | number | undefined> = {}
): Promise<T> {
    const formData = new FormData();
    formData.append('action', action);
    formData.append('nonce', String(getFavouritesConfig().nonce || ''));

    Object.entries(fields).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }

        const normalizedValue = String(value);
        if (normalizedValue.trim() === '') {
            return;
        }

        formData.append(key, normalizedValue);
    });

    const response = await fetch(getAjaxUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success !== true) {
        throw new Error(normalizeResponseMessage(payload, 'Favourite request failed.'));
    }

    return payload.data as T;
}
